import type { Narrator, NarratorCapabilities, PlayOptions, PlaybackHandle, PrepareRequest, PreparedNarration } from "./narrator.js";
import { playPreparedBuffer } from "./buffer-playback.js";
import {
  AUDIO_CACHE_URL_VERSION,
  AUDIO_CHUNK_PAUSE_SEC,
  AUDIO_CODECS,
  audioCacheKey,
  audioKeyParts,
} from "./cache-key.js";
import { narrationSpeed, type NarrationEngine } from "./voices.js";

export {
  AUDIO_CACHE_ARTIFACT,
  AUDIO_CACHE_URL_VERSION,
  AUDIO_CHUNK_PAUSE_SEC,
  AUDIO_CODEC,
  AUDIO_CODECS,
  AUDIO_NORMALIZATION_VERSION,
  AUDIO_SAMPLE_RATE,
  AUDIO_SPEED,
  AUDIO_VOICE,
  audioCacheKey,
  audioCacheKeys,
  audioKeyParts,
} from "./cache-key.js";
export type { AudioArtifact, AudioKeyInput } from "./cache-key.js";

/**
 * Shared, pre-effects narration audio.
 *
 * The station is deterministic: the same pool entry, read by the same voice at
 * the same speed, renders the same dry audio for every listener. Caching that
 * audio lets a listener skip both the model download and inference when the
 * disclosure has already been rendered somewhere else.
 *
 * All audio in it is produced by the Worker, so a hit is always playable dry
 * audio, with the voice profile applied after the effects chain. The client
 * only reads: there is no model left on this side that could render a miss.
 *
 * The cache lives behind a Worker + R2. Reads are a HEAD probe with a tight
 * budget and a bounded GET. Writes belong to the operator seed job.
 */

/** How long a cache probe may take before the station falls back to today's path. */
export const AUDIO_PROBE_BUDGET_MS = 800;
/**
 * How long a cache download may take once the probe has said the entry exists.
 *
 * Longer than the probe because this moves real bytes, but still bounded: a
 * stuck GET must not hold the scheduler's single worker until the watchdog.
 *
 * Sized against the producer's own output, not against optimism: MeloTTS
 * returns about 3 MB for a 33 s entry (44.1 kHz mono at 705 kbps). Five seconds
 * demanded 4.7 Mbps and turned a slow phone into a music-only slot; fifteen
 * covers 1.6 Mbps, which is still well inside the lead the scheduler keeps.
 */
export const AUDIO_FETCH_BUDGET_MS = 15_000;
const DISABLED_VALUES = new Set(["", "0", "false", "off", "none"]);

export function audioCacheUrl(origin: string, key: string): string {
  return `${origin}/audio/${AUDIO_CACHE_URL_VERSION}/${key}`;
}

function normalizeOrigin(value: string): string | null {
  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
}

/**
 * Resolve the cache origin from `?audio-cache=` first, then the build-time
 * setting. Empty values and `off` disable the cache, which is the default: an
 * unconfigured build must make no cache requests at all.
 */
export function resolveAudioCacheOrigin(search: string, configured?: string): string | null {
  let fromQuery: string | null = null;
  try {
    fromQuery = new URLSearchParams(search).get("audio-cache");
  } catch {
    fromQuery = null;
  }
  const value = fromQuery ?? configured ?? "";
  if (DISABLED_VALUES.has(value.trim().toLowerCase())) return null;
  return normalizeOrigin(value.trim());
}

export interface AudioCacheFetchOptions {
  fetchImpl?: typeof fetch;
  signal?: AbortSignal;
}

function fetcher(options: AudioCacheFetchOptions): typeof fetch {
  return options.fetchImpl ?? globalThis.fetch.bind(globalThis);
}

/**
 * Is the disclosure already cached?
 *
 * HEAD, not GET: the probe runs before the model loads and must not pay for
 * bytes it may not use. Any error, timeout or non-2xx is a miss, never a
 * failure: a slow or broken cache must not block narration.
 */
export async function probeAudioCache(
  origin: string,
  key: string,
  options: AudioCacheFetchOptions & { budgetMs?: number } = {},
): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), options.budgetMs ?? AUDIO_PROBE_BUDGET_MS);
  try {
    const response = await fetcher(options)(audioCacheUrl(origin, key), {
      method: "HEAD",
      cache: "no-store",
      signal: controller.signal,
    });
    return response.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/** Fetch the cached dry audio, or null when it is not there. */
export async function fetchCachedAudio(
  origin: string,
  key: string,
  options: AudioCacheFetchOptions & { budgetMs?: number } = {},
): Promise<ArrayBuffer | null> {
  if (options.signal?.aborted) return null;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), options.budgetMs ?? AUDIO_FETCH_BUDGET_MS);
  const forwardAbort = () => controller.abort();
  options.signal?.addEventListener("abort", forwardAbort, { once: true });
  try {
    const response = await fetcher(options)(audioCacheUrl(origin, key), {
      signal: controller.signal,
    });
    if (!response.ok) return null;
    const bytes = await response.arrayBuffer();
    return bytes.byteLength > 0 ? bytes : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener("abort", forwardAbort);
  }
}

/** Thrown when a prepared slot was expected in the cache but is not there. */
export class AudioCacheMissError extends Error {
  constructor(readonly key: string) {
    super(`narration not cached: ${key}`);
    this.name = "AudioCacheMissError";
  }
}

export interface CachedNarratorOptions {
  context: BaseAudioContext;
  voiceBus: AudioNode;
  origin: string;
  /** Which producer's keys to read; defaults to Kokoro. */
  engine?: NarrationEngine;
  voice?: string;
  speed?: number;
  chunkPauseSec?: number;
  fetchImpl?: typeof fetch;
  /** Codec preference; defaults to the engine's codecs. */
  codecs?: readonly string[];
}

/**
 * A narrator backed entirely by the cache.
 *
 * It is seekable and graph-routable like Kokoro because a decoded buffer is the
 * same kind of object, so the player, ducking and effects treat a hit exactly
 * like a local render. `prepare` throws `AudioCacheMissError` rather than
 * returning empty narration, so the caller can decide whether a live fallback
 * exists.
 */
export class CachedNarrator implements Narrator {
  readonly id = "cached" as const;
  readonly capabilities: NarratorCapabilities = {
    graphRoutable: true,
    volumeControl: true,
    seekable: true,
    duckable: true,
  };

  constructor(private readonly options: CachedNarratorOptions) {}

  async init(): Promise<void> {
    // Nothing to load: the model is the thing this path exists to avoid.
  }

  async prepare(request: PrepareRequest, signal: AbortSignal): Promise<PreparedNarration> {
    const engine = this.options.engine ?? "kokoro";
    const codecs = this.options.codecs ?? (engine === "melotts" ? ["mp3"] : AUDIO_CODECS);
    const parts = audioKeyParts(engine, request, this.options.chunkPauseSec ?? AUDIO_CHUNK_PAUSE_SEC);
    let preferredKey = "";
    for (const codec of codecs) {
      const key = await audioCacheKey({
        entryId: request.entryId,
        text: request.text,
        chunks: parts.chunks,
        chunkPauseSec: parts.chunkPauseSec,
        voice: this.options.voice,
        // The rate belongs to the entry, and the key covers it: a caller that
        // forgot it would address a clip nobody wrote.
        speed: this.options.speed ?? narrationSpeed(engine, request.entryId),
        engine,
        codec,
      });
      if (!preferredKey) preferredKey = key;
      const bytes = await fetchCachedAudio(this.options.origin, key, {
        fetchImpl: this.options.fetchImpl,
        signal,
      });
      if (!bytes) {
        if (signal.aborted) throw new DOMException("aborted", "AbortError");
        continue;
      }
      if (signal.aborted) throw new DOMException("aborted", "AbortError");
      try {
        // `decodeAudioData` detaches its input, so hand it a copy.
        const buffer = await this.options.context.decodeAudioData(bytes.slice(0));
        if (signal.aborted) throw new DOMException("aborted", "AbortError");
        return {
          slotIndex: request.slotIndex,
          entryId: request.entryId,
          text: request.text,
          buffer,
          durationSec: buffer.duration,
        };
      } catch (error) {
        // A container this browser cannot decode: try the next codec.
        if (signal.aborted) throw error;
      }
    }
    throw new AudioCacheMissError(preferredKey);
  }

  play(prepared: PreparedNarration, options: PlayOptions): PlaybackHandle {
    if (!prepared.buffer) throw new Error("cached narration requires a buffer");
    return playPreparedBuffer(this.options.context, this.options.voiceBus, prepared, options);
  }

  dispose(): void {
    // No owned resources.
  }
}

/**
 * Encode a rendered buffer as a mono 16-bit WAV.
 *
 * The browser has no MP3 encoder, so this is the only container it can produce;
 * it is what the browser tests build fixtures with, and what a WAV-keyed object
 * would need. The key records the codec, so both can coexist.
 */
export function encodeWav(buffer: AudioBuffer): Uint8Array {
  const channels = 1;
  const bitsPerSample = 16;
  const frames = buffer.length;
  const sampleRate = buffer.sampleRate;
  const blockAlign = (channels * bitsPerSample) / 8;
  const dataBytes = frames * blockAlign;
  const bytes = new Uint8Array(44 + dataBytes);
  const view = new DataView(bytes.buffer);

  const ascii = (offset: number, text: string) => {
    for (let index = 0; index < text.length; index += 1) {
      view.setUint8(offset + index, text.charCodeAt(index));
    }
  };

  ascii(0, "RIFF");
  view.setUint32(4, 36 + dataBytes, true);
  ascii(8, "WAVE");
  ascii(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, channels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  ascii(36, "data");
  view.setUint32(40, dataBytes, true);

  const samples = buffer.getChannelData(0);
  let offset = 44;
  for (let index = 0; index < frames; index += 1) {
    const sample = Math.max(-1, Math.min(1, samples[index] ?? 0));
    view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
    offset += 2;
  }
  return bytes;
}
